/**
 * Sound: optional, off by default, synthesised with WebAudio (no assets).
 *
 *   claim         a pentatonic pluck, pitched by slot index
 *   refusal       a 110 Hz filtered thump
 *   ask arriving  two notes
 *   displacement  a gliss
 *   peers         6 dB down, panned by where on the stage it happened
 *
 * The master sits at −18 dB and mutes while the tab is hidden. A limiter
 * keeps a backlog of seals from turning into a storm. The pitch table, the
 * decibel maths and the limiter are pure and tested; the rest is WebAudio.
 */

import type { SoundEvent } from './conductor';

export const MASTER_DB = -18;
export const PEER_DB = -6;
export const PENTATONIC = [0, 2, 4, 7, 9] as const;
export const BASE_HZ = 220;

export const dbToGain = (db: number): number => 10 ** (db / 20);

/** A slot's pluck: the major pentatonic from A3, wrapping every three octaves. */
export const pluckHz = (slot: number, base = BASE_HZ): number => {
  const deg = ((Math.floor(slot) % 15) + 15) % 15;
  const octave = Math.floor(deg / 5);
  const step = PENTATONIC[deg % 5] as number;
  return base * 2 ** ((12 * octave + step) / 12);
};

/** At most `max` voices started in any `windowMs`. */
export class VoiceLimiter {
  private starts: number[] = [];
  constructor(readonly max = 6, readonly windowMs = 250) {}
  allow(now: number): boolean {
    this.starts = this.starts.filter((t) => now - t < this.windowMs);
    if (this.starts.length >= this.max) return false;
    this.starts.push(now);
    return true;
  }
}

export interface Sound {
  readonly on: boolean;
  setOn(on: boolean): void;
  /** A user gesture happened: a context made before one (sound restored from a saved choice) may start now. */
  unlock(): void;
  setHidden(hidden: boolean): void;
  play(e: SoundEvent): void;
  destroy(): void;
}

type Ctor = new () => AudioContext;

export const createSound = (opts: { audio?: () => AudioContext | null } = {}): Sound => {
  let on = false;
  let hidden = false;
  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;
  const limiter = new VoiceLimiter();

  const make = (): AudioContext | null => {
    if (opts.audio) return opts.audio();
    const g = globalThis as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor };
    const C = g.AudioContext ?? g.webkitAudioContext;
    try {
      return C ? new C() : null;
    } catch {
      return null;
    }
  };

  const level = () => (on && !hidden ? dbToGain(MASTER_DB) : 0);

  const setLevel = () => {
    if (!ctx || !master) return;
    master.gain.setTargetAtTime(level(), ctx.currentTime, 0.03);
  };

  const ensure = (): boolean => {
    if (!ctx) {
      ctx = make();
      if (!ctx) return false;
      master = ctx.createGain();
      master.gain.value = 0;
      master.connect(ctx.destination);
    }
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
    return true;
  };

  /** A voice's output: its own gain, then a pan, then the master. */
  const voice = (pan: number, db: number): GainNode | null => {
    if (!ctx || !master) return null;
    const g = ctx.createGain();
    g.gain.value = 0;
    const p = ctx.createStereoPanner();
    p.pan.value = Math.max(-1, Math.min(1, pan));
    const trim = ctx.createGain();
    trim.gain.value = dbToGain(db);
    g.connect(p).connect(trim).connect(master);
    return g;
  };

  const envelope = (g: GainNode, t: number, attack: number, peak: number, decay: number) => {
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(peak, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  };

  const note = (freq: number, t: number, o: { type: OscillatorType; decay: number; pan: number; db: number; cutoff: number; to?: number }) => {
    if (!ctx) return;
    const g = voice(o.pan, o.db);
    if (!g) return;
    const osc = ctx.createOscillator();
    osc.type = o.type;
    osc.frequency.setValueAtTime(freq, t);
    if (o.to !== undefined) osc.frequency.exponentialRampToValueAtTime(o.to, t + o.decay);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = o.cutoff;
    osc.connect(lp).connect(g);
    envelope(g, t, 0.005, 0.9, o.decay);
    osc.start(t);
    osc.stop(t + o.decay + 0.05);
    osc.onended = () => {
      osc.disconnect();
      lp.disconnect();
      g.disconnect();
    };
  };

  return {
    get on() {
      return on;
    },
    setOn(next) {
      on = next;
      if (on) ensure();
      setLevel();
    },
    setHidden(h) {
      hidden = h;
      setLevel();
    },
    unlock() {
      if (on) ensure();
    },
    play(e) {
      if (!on || hidden || !ctx) return;
      // made before any gesture, the context starts suspended; once the page has been used, it may run
      if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
      if (!limiter.allow(performance.now())) return;
      const t = ctx.currentTime + 0.01;
      switch (e.kind) {
        case 'pluck':
          note(pluckHz(e.slot), t, { type: 'triangle', decay: 0.45, pan: e.pan, db: e.peer ? PEER_DB : 0, cutoff: 2400 });
          break;
        case 'thump':
          note(110, t, { type: 'sine', decay: 0.3, pan: 0, db: 0, cutoff: 320, to: 70 });
          break;
        case 'ask':
          note(659.25, t, { type: 'sine', decay: 0.22, pan: e.pan, db: -3, cutoff: 3000 });
          note(880, t + 0.09, { type: 'sine', decay: 0.26, pan: e.pan, db: -3, cutoff: 3000 });
          break;
        case 'gliss':
          note(660, t, { type: 'sawtooth', decay: 0.42, pan: e.pan, db: -8, cutoff: 1200, to: 220 });
          break;
      }
    },
    destroy() {
      on = false;
      master?.disconnect();
      void ctx?.close().catch(() => {});
      ctx = null;
      master = null;
    },
  };
};
